import { Trans, useLingui } from '@lingui/react/macro';
import { Eye, Maximize2, Plus, Upload } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { ImportShiftsModal } from './import-shifts-modal';
import { PreviewModal } from '@/components/template-preview/preview-modal';
import { downloadTemplateJson } from '@/domain/templates/export-template-json';
import { countIndividualShifts } from '@/domain/templates/preview-stats';
import { useEmployees } from '@/hooks/use-employees';
import { useShiftTypes } from '@/hooks/use-shift-types';
import { useSites } from '@/hooks/use-sites';
import { useStaffGroups } from '@/hooks/use-staff-groups';
import { useTags } from '@/hooks/use-tags';
import { useToast } from '@/hooks/use-toast';
import { useWizardState, type LifecycleStatus } from '@/hooks/use-wizard-state';
import type { SavedTemplate } from '@/types/saved-template';
import type { ShiftSet, TemplateBasics } from '@/types/template';

import { AddPatternModal } from './add-pattern-modal';
import { BasicsModal } from './basics-modal';
import { BuildHeader } from './build-header';
import { LivePreview } from './live-preview';
import { PatternCard } from './pattern-card';
import { PatternEmptyState } from './pattern-empty-state';
import { PatternForm } from './pattern-form';

/**
 * Props shared by the two template-authoring shells (the legacy wizard and the
 * v2 Build workspace), so {@link TemplateBuilderEntry} can swap between them.
 */
export interface TemplateBuilderProps {
  /** The template being edited, or `null` for the create-new flow. */
  initialTemplate: SavedTemplate | null;
  /**
   * Returns to the templates list; receives the template's id when it was
   * published during this visit, so the list can highlight it.
   */
  onBack: (publishedTemplateId?: string) => void;
  /**
   * Called once a new template is first saved, so the host can move to the
   * template's own URL. Omit when editing an existing template.
   */
  onCreated?: (templateId: string) => void;
}

/**
 * v2 single-page Build workspace — the two-pane replacement for the 4-step
 * wizard, gated by the `templateBuilder.buildWorkspace` flag. The left pane
 * lists shift patterns (with an inline add/edit form); the right pane shows the
 * live preview. Shares the wizard's state (`useWizardState`) so the persisted
 * template shape is unchanged.
 *
 * A new template opens the {@link BasicsModal} in create mode first (dismissing
 * it abandons the flow); the header's "Edit details" re-opens it in edit mode.
 * Save and Publish/Unpublish are separate actions: Save writes the definition
 * and never changes the lifecycle; Publish/Unpublish changes only the lifecycle
 * and is blocked while there are unsaved changes.
 * The open pattern form mirrors into the preview as a draft (ghost) pill. Demand
 * scaling + the DemandControl land in a later phase.
 */
export function BuildWorkspace({ initialTemplate, onBack, onCreated }: TemplateBuilderProps) {
  const { t } = useLingui();
  const { toast } = useToast();
  const wizard = useWizardState(initialTemplate);
  const shifts = wizard.state.shifts;
  const basics = wizard.state.templateBasics;

  // Staff groups, tags + employees scope to the template's selected sites,
  // matching the wizard; they feed the Excel importer's validation and the
  // pattern form's "restrict who can be assigned" pickers.
  const scopedSiteIds = basics.scope.sites?.length
    ? basics.scope.sites.map((site) => site.id)
    : undefined;
  const shiftTypes = useShiftTypes().data ?? [];
  const sites = useSites().data ?? [];
  const staffGroups = useStaffGroups(scopedSiteIds).data ?? [];
  const tags = useTags(scopedSiteIds).data ?? [];
  const employees = useEmployees(scopedSiteIds).data ?? [];

  const [form, setForm] = useState<FormState>(null);
  // The in-progress pattern from the open form, mirrored into the preview as a
  // ghost (draft) pill. Cleared whenever the form closes.
  const [draft, setDraft] = useState<ShiftSet | null>(null);
  // Ref to access PatternForm's save function when using static footer
  const patternFormSaveRef = useRef<(() => void) | null>(null);
  // Whether the open form is currently valid, mirrored from PatternForm so the
  // static footer's Save button can be disabled until required fields are filled.
  const [formCanSave, setFormCanSave] = useState(false);
  // The basics modal: opened in create mode for a brand-new template (before the
  // workspace is usable), or in edit mode from the header.
  const [detailsModal, setDetailsModal] = useState<DetailsModalState>(
    initialTemplate === null ? { mode: 'create' } : null,
  );
  const [importOpen, setImportOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  // The "Add pattern" chooser (starter / from-scratch / import), shown once the
  // template already has patterns.
  const [addOpen, setAddOpen] = useState(false);
  // Whether the template was published during this visit, so Back can tell the
  // list to highlight it.
  const [publishedDuringVisit, setPublishedDuringVisit] = useState(false);

  const name = basics.name?.trim() || t`Untitled template`;

  // While editing, drop the edited pattern's solid pills so the draft ghost
  // shows the live edit in its place rather than alongside the saved version.
  const editingId = form?.mode === 'edit' ? form.shift.id : null;
  const previewShifts = editingId ? shifts.filter((shift) => shift.id !== editingId) : shifts;

  const patternCount = shifts.length;
  const individualShiftCount = countIndividualShifts(shifts);
  const patternCountLabel = patternCount === 1 ? t`1 pattern` : t`${patternCount} patterns`;
  const individualShiftLabel =
    individualShiftCount === 1
      ? t`1 individual shift`
      : t`${individualShiftCount} individual shifts`;

  function closeForm() {
    setForm(null);
    setDraft(null);
  }

  // Open the inline add form, optionally pre-filled from a starter, and close
  // the chooser. Shared by the empty-pane choices and the "Add pattern" modal.
  function startPattern(prefill: ShiftSet | null) {
    setForm({ mode: 'add', prefill: prefill ?? undefined });
    setAddOpen(false);
  }

  function handleSave(shift: ShiftSet) {
    if (form?.mode === 'edit') {
      wizard.updateShift(form.shift.id, shift);
    } else {
      wizard.addShift(shift);
    }
    closeForm();
  }

  function handleDuplicate(shift: ShiftSet) {
    wizard.addShift({ ...shift, id: crypto.randomUUID(), name: t`${shift.name} (copy)` });
  }

  // Bulk-add every validated row from the Excel importer, then close it.
  function handleImport(parsedShifts: ShiftSet[]) {
    for (const shift of parsedShifts) wizard.addShift(shift);
    setImportOpen(false);
  }

  function submitBasics(next: TemplateBasics) {
    wizard.updateTemplateBasics(next);
    setDetailsModal(null);
  }

  /**
   * Dismissing the create modal abandons the new template; dismissing an edit
   * just closes it and leaves the workspace as-is.
   */
  function dismissBasics() {
    if (detailsModal?.mode === 'create') {
      onBack();
    } else {
      setDetailsModal(null);
    }
  }

  /**
   * Saves the definition; lifecycle is untouched. Stays in the workspace either way.
   */
  async function handleSaveTemplate() {
    const outcome = await wizard.saveToBackend();
    if (outcome.kind === 'failed') {
      toast({
        title: t`Save failed`,
        description: t`Your changes couldn't be saved. Try again.`,
        variant: 'destructive',
      });
      return;
    }
    toast({ title: t`Template saved` });
    if (outcome.created) onCreated?.(outcome.id);
  }

  /**
   * Publishes a draft or unpublishes a published template, without saving.
   */
  async function handleToggleLifecycle() {
    const target: LifecycleStatus = wizard.lifecycleStatus === 'published' ? 'draft' : 'published';
    const succeeded = await wizard.changeLifecycle(target);
    if (!succeeded) {
      toast({
        title: target === 'published' ? t`Publish failed` : t`Unpublish failed`,
        description:
          target === 'published'
            ? t`The template couldn't be published. Try again.`
            : t`The template couldn't be unpublished. Try again.`,
        variant: 'destructive',
      });
      return;
    }
    // Only a template that is published when the visit ends should be highlighted.
    setPublishedDuringVisit(target === 'published');
    toast({ title: target === 'published' ? t`Template published` : t`Template unpublished` });
  }

  return (
    <div className="flex flex-col gap-4 p-4 rotaready:p-0" data-testid="build-workspace">
      <BuildHeader
        name={name}
        scope={basics.scope}
        siteCount={basics.scope.sites?.length ?? 0}
        description={basics.description}
        status={wizard.lifecycleStatus}
        advancedSettings={wizard.advancedSettings}
        onAdvancedChange={wizard.updateAdvancedSettings}
        onEditDetails={() => setDetailsModal({ mode: 'edit' })}
        onCancel={() => onBack(publishedDuringVisit ? (wizard.templateId ?? undefined) : undefined)}
        onSave={() => void handleSaveTemplate()}
        onToggleLifecycle={() => void handleToggleLifecycle()}
        onExportJson={() => downloadTemplateJson(wizard.state, name)}
        canPublish={shifts.length > 0}
        lifecycleBlockedReason={
          wizard.templateId === null || wizard.hasUnsavedChanges ? 'unsaved' : null
        }
        saving={wizard.isPersisting}
      />

      <div className="max-h-[85vh] overflow-hidden rounded-lg border bg-card rotaready:mx-4 rotaready:mb-4">
        <div className="grid h-full min-h-0 grid-rows-1 grid-cols-[460px_1fr] divide-x">
          <section
            aria-label="Shift patterns"
            className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden"
          >
            {/* Static header - always visible */}
            <header className="flex items-center justify-between gap-2 border-b px-4 py-3 flex-shrink-0">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold">
                  <Trans>Shift patterns</Trans>
                </h2>
                <p className="text-xs text-muted-foreground">
                  {patternCountLabel} · {individualShiftLabel}
                </p>
              </div>
              {!form && (
                <Button size="sm" onClick={() => setAddOpen(true)}>
                  <Plus className="size-4" />
                  <Trans>Add pattern</Trans>
                </Button>
              )}
            </header>

            {/* Scrollable middle section */}
            <div className="min-h-0 flex-1 overflow-y-auto">
              {form ? (
                <div className="p-4">
                  <PatternForm
                    initial={form.mode === 'edit' ? form.shift : (form.prefill ?? null)}
                    shiftTypes={shiftTypes}
                    availableStaffGroups={staffGroups}
                    availableTags={tags}
                    availableEmployees={employees}
                    onSave={handleSave}
                    onCancel={closeForm}
                    onDraftChange={setDraft}
                    onCanSaveChange={setFormCanSave}
                    hideFooter
                    saveRef={patternFormSaveRef}
                    demandScaling={basics.demandScaling}
                  />
                </div>
              ) : shifts.length === 0 ? (
                <div className="p-4">
                  <PatternEmptyState onStart={startPattern} onImport={() => setImportOpen(true)} />
                </div>
              ) : (
                <div className="flex flex-col gap-3 p-4">
                  {shifts.map((shift) => (
                    <PatternCard
                      key={shift.id}
                      shift={shift}
                      onEdit={() => setForm({ mode: 'edit', shift })}
                      onDuplicate={() => handleDuplicate(shift)}
                      onDelete={() => wizard.deleteShift(shift.id)}
                    />
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="self-center text-muted-foreground"
                    onClick={() => setImportOpen(true)}
                  >
                    <Upload className="size-4" />
                    <Trans>Import from Excel</Trans>
                  </Button>
                </div>
              )}
            </div>

            {/* Static footer - always visible */}
            {form && (
              <div className="border-t px-4 py-3 flex-shrink-0">
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={closeForm}>
                    <Trans>Cancel</Trans>
                  </Button>
                  <Button
                    size="sm"
                    disabled={!formCanSave}
                    onClick={() => patternFormSaveRef.current?.()}
                  >
                    <Trans>Save</Trans>
                  </Button>
                </div>
              </div>
            )}
          </section>

          <section
            aria-label="Live preview"
            className="flex min-h-0 min-w-0 flex-col gap-3 overflow-hidden p-4"
          >
            <div className="flex items-center justify-between gap-2">
              <h2 className="flex min-w-0 items-center gap-2 text-sm font-semibold">
                <Eye className="size-4 shrink-0 text-muted-foreground" />
                <Trans>Rota preview</Trans>
              </h2>
              <Button
                variant="ghost"
                size="icon"
                aria-label={t`Open larger preview`}
                disabled={shifts.length === 0}
                onClick={() => setPreviewOpen(true)}
              >
                <Maximize2 className="size-4" />
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              <LivePreview
                shifts={previewShifts}
                draftShift={form ? draft : null}
                demandScaling={basics.demandScaling}
              />
            </div>
          </section>
        </div>
      </div>

      <BasicsModal
        mode={detailsModal?.mode ?? 'edit'}
        open={detailsModal !== null}
        initialBasics={basics}
        sites={sites}
        onSubmit={submitBasics}
        onDismiss={dismissBasics}
      />

      {importOpen && (
        <ImportShiftsModal
          shiftTypes={shiftTypes}
          staffGroups={staffGroups}
          tags={tags}
          onImport={handleImport}
          onCancel={() => setImportOpen(false)}
        />
      )}

      {previewOpen && <PreviewModal shifts={shifts} onClose={() => setPreviewOpen(false)} />}

      <AddPatternModal
        open={addOpen}
        onStart={startPattern}
        onImport={() => {
          setImportOpen(true);
          setAddOpen(false);
        }}
        onClose={() => setAddOpen(false)}
      />
    </div>
  );
}

/** Basics-modal state: closed, or open in create or edit mode. */
type DetailsModalState = { mode: 'create' | 'edit' } | null;

/** Left-pane form state: closed, adding a new pattern (optionally pre-filled
 * from a starter), or editing an existing one. */
type FormState = { mode: 'add'; prefill?: ShiftSet } | { mode: 'edit'; shift: ShiftSet } | null;
